/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  Button,
  message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getEstadosGlobales, getTiposDeNomina } from 'api/catalogos';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { getSubtiposNominas } from 'api/nominas/subtiposDeNominas';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import { onError } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import { ESTATUS_ACTIVO, ESTATUS_INACTIVO } from 'utils/constants';
import {
  getAcumuladosNominasColumnas,
  getAcumuladoNominaColumna,
  postAcumuladoNominaColumna,
  patchAcumuladoNominaColumna,
  deleteAcumuladoNominaColumna,
  descargarExcelNominasAcumuladas,
} from 'api/nominas/acumuladosNominasColumnas';

export const acumuladoNominaColumnaPermission = {
  permissionModel: 'acumuladonominacolumna',
  catalogsModels: [],
  tabsModels: [],
};

const OPCIONES_SI_NO = [
  { id: true, descripcion: 'Sí' },
  { id: false, descripcion: 'No' },
];

const descargarBlobComoArchivo = (blob, nombreArchivo) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  link.click();
  URL.revokeObjectURL(url);
};

const resumen = (ids, catalogo, prop) => (ids || [])
  .map((id) => catalogo.find((e) => e.id === id)?.[prop])
  .filter(Boolean)
  .join(', ');

const AcumuladosNominasColumnas = ({ permission }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [estados, setEstados] = useState([]);
  const [tiposDeNominasEmpleados, setTiposDeNominasEmpleados] = useState([]);
  const [tiposDeNominas, setTiposDeNominas] = useState([]);
  const [subtiposDeNominas, setSubtiposDeNominas] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [openDescarga, setOpenDescarga] = useState(false);
  const [ejercicio, setEjercicio] = useState();
  const [form] = Form.useForm();
  const esTotal = Form.useWatch('es_total', form);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getAcumuladosNominasColumnas();
    setData(_data.map((e) => formatReceivedValues(e)));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tiposEmpleados,
        _tiposNominas,
        _subtipos,
        _periodos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeNominasEmpleados(),
        getTiposDeNomina(),
        getSubtiposNominas(),
        getPeriodosFiscales(),
        getAcumuladosNominasColumnas(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposDeNominasEmpleados(_tiposEmpleados);
        setTiposDeNominas(_tiposNominas);
        setSubtiposDeNominas(_subtipos);
        // el selector trabaja con el id: se usa el año (ejercicio) como id
        setPeriodosFiscales(_periodos.map((p) => ({ id: p.periodo, descripcion: String(p.periodo) })));
        setData(_data.map((e) => formatReceivedValues(e)));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setOpen(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    setOpen(true);
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const columna = await getAcumuladoNominaColumna(key);
    setLoading(false);
    if (columna) {
      form.resetFields();
      setTimeout(() => {
        form.setFieldsValue(formatReceivedValues(columna));
        setOpen(true);
      });
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      // Una columna de total no usa criterios y una normal no suma columnas
      const payload = values.es_total
        ? {
          ...values, tipos_de_nominas_empleados: [], tipos_de_nominas: [], subtipos_de_nominas: [],
        }
        : { ...values, columnas_a_sumar: [] };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchAcumuladoNominaColumna(key, payload)
        : postAcumuladoNominaColumna(payload));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key && await deleteAcumuladoNominaColumna(key)) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onDescargar = async () => {
    if (!ejercicio) {
      message.warning('Seleccione el Ejercicio');
      return;
    }
    setLoading(true);
    const { blob, error } = await descargarExcelNominasAcumuladas(ejercicio);
    setLoading(false);
    if (error) {
      message.error(error);
      return;
    }
    descargarBlobComoArchivo(blob, `Nominas_Acumuladas_${ejercicio}.xlsx`);
    setOpenDescarga(false);
  };

  const onClickDescarga = () => {
    if (!ejercicio && periodosFiscales.length) {
      const actual = periodosFiscales.find((p) => p.id === new Date().getFullYear());
      setEjercicio((actual || periodosFiscales[periodosFiscales.length - 1]).id);
    }
    setOpenDescarga(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const requiredRule = { required: true, message: 'El campo es requerido' };
  const requiredMultiple = [{ required: true, message: 'Seleccione al menos una opción' }];

  const columns = [
    {
      titleText: 'Orden',
      dataIndex: 'orden',
      key: 'orden',
      width: 90,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 220,
    },
    {
      titleText: 'Tipos de Nómina Empleado',
      dataIndex: 'tipos_de_nominas_empleados',
      key: 'tipos_de_nominas_empleados',
      width: 250,
      render: (val) => resumen(val, tiposDeNominasEmpleados, 'descripcion'),
    },
    {
      titleText: 'Tipos de Nómina',
      dataIndex: 'tipos_de_nominas',
      key: 'tipos_de_nominas',
      width: 250,
      render: (val) => resumen(val, tiposDeNominas, 'descripcion'),
    },
    {
      titleText: 'Subtipos de Nómina',
      dataIndex: 'subtipos_de_nominas',
      key: 'subtipos_de_nominas',
      width: 250,
      render: (val) => resumen(val, subtiposDeNominas, 'descripcion'),
    },
    {
      titleText: 'Columnas a Sumar',
      dataIndex: 'columnas_a_sumar',
      key: 'columnas_a_sumar',
      width: 250,
      render: (val) => resumen(val, data, 'nombre'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const nombreSeleccionado = data.find((e) => e.id === selectedRowKeys[0])?.nombre;
  const ordenColumna = data.find((e) => e.id === selectedRowKeys[0])?.orden;
  const columnasSumables = data.filter((e) => e.id !== selectedRowKeys[0]
    && (ordenColumna === undefined || e.orden < ordenColumna));

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={{ selectedRowKeys, type: 'radio' }}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: onClickDescarga,
              text: 'Descargar Excel',
              icon: <DownloadOutlined />,
              className: 'success',
              disabledBySelection: false,
              disabled: !data.length,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width="55%"
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Columna de Nóminas Acumuladas`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="acumulado"
          >
            <Row gutter={10}>
              <Col xs={24} md={12}>
                <Form.Item name="es_total" label="Es Columna de Total" rules={[requiredRule]} initialValue={false}>
                  <Select labelProp="descripcion" keyProp="id" dataSource={OPCIONES_SI_NO} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[requiredRule]}
                  initialValue={ESTATUS_ACTIVO}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados.filter((e) => [ESTATUS_ACTIVO, ESTATUS_INACTIVO].includes(e.id))}
                    disabled={!selectedRowKeys.length}
                  />
                </Form.Item>
              </Col>
              {esTotal ? (
                <Col span={24}>
                  <Form.Item
                    name="columnas_a_sumar"
                    label="Columnas a Sumar"
                    rules={requiredMultiple}
                  >
                    <Select
                      mode="multiple"
                      labelProp="nombre"
                      keyProp="id"
                      dataSource={columnasSumables}
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>
                  <Col span={24}>
                    <Form.Item name="tipos_de_nominas_empleados" label="Tipos de Nómina Empleado" rules={requiredMultiple}>
                      <Select
                        mode="multiple"
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={tiposDeNominasEmpleados}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="tipos_de_nominas" label="Tipos de Nómina" rules={requiredMultiple}>
                      <Select
                        mode="multiple"
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={tiposDeNominas}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="subtipos_de_nominas"
                      label="Subtipos de Nómina Especial"
                      rules={requiredMultiple}
                    >
                      <Select
                        mode="multiple"
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={subtiposDeNominas}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col xs={24} md={12}>
                <Form.Item name="muestra_percepciones" label="Presentar Total Percepciones" initialValue>
                  <Select labelProp="descripcion" keyProp="id" dataSource={OPCIONES_SI_NO} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="muestra_deducciones_y_descuentos" label="Presentar Total Deducciones y Descuentos" initialValue>
                  <Select labelProp="descripcion" keyProp="id" dataSource={OPCIONES_SI_NO} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="muestra_neto" label="Presentar Neto a Pagar" initialValue>
                  <Select labelProp="descripcion" keyProp="id" dataSource={OPCIONES_SI_NO} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="muestra_numero_de_empleados" label="Presentar No. de Empleados" initialValue>
                  <Select labelProp="descripcion" keyProp="id" dataSource={OPCIONES_SI_NO} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
        <Modal
          open={openDescarga}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={450}
          title="Descargar Nóminas Acumuladas"
          onCancel={() => setOpenDescarga(false)}
        >
          <Form layout="vertical">
            <Form.Item label="Ejercicio">
              <Select
                labelProp="descripcion"
                keyProp="id"
                dataSource={periodosFiscales}
                value={ejercicio}
                onChange={setEjercicio}
              />
            </Form.Item>
          </Form>
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={() => setOpenDescarga(false)}>Cancelar</Button>
            </Col>
            <Col>
              <Button type="primary" icon={<DownloadOutlined />} onClick={onDescargar} loading={loading}>
                Descargar
              </Button>
            </Col>
          </Row>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Columna ${nombreSeleccionado || ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default AcumuladosNominasColumnas;
